import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Input,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import PageTitle from '../../components/PageTitle';
import { adminVoiceLowBalanceDayQuery } from '../../api';

const { Text } = Typography;

const todayStr = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const statusColor = (text) => {
  const t = String(text || '');
  if (t === '成功' || t === '已接听') return 'success';
  if (t === '失败' || t === '未接听') return 'error';
  if (t === '队列中' || t === '正在拨打' || t === '同步中' || t === '尚未拨完') return 'processing';
  return 'default';
};

const calcDialStats = (historyList) => {
  let publicOut = 0;
  let exclusiveOut = 0;
  let publicAnswered = 0;
  let exclusiveAnswered = 0;
  const list = Array.isArray(historyList) ? historyList : [];
  for (const row of list) {
    const pubOk = row?.public_status === '成功';
    const exOk = row?.exclusive_status === '成功';
    const answered = row?.answer_label === '已接听' || row?.answer_status === 'answered';
    if (pubOk) publicOut += 1;
    if (exOk) exclusiveOut += 1;
    if (answered && pubOk && !exOk) publicAnswered += 1;
    if (answered && exOk) exclusiveAnswered += 1;
  }
  const rate = (a, b) => (b > 0 ? `${((a / b) * 100).toFixed(1)}%` : '—');
  return {
    publicOut,
    exclusiveOut,
    publicAnswered,
    exclusiveAnswered,
    publicRate: rate(publicAnswered, publicOut),
    exclusiveRate: rate(exclusiveAnswered, exclusiveOut),
    historyTotal: list.length,
  };
};

const listDatesInclusive = (begin, end) => {
  const out = [];
  const [by, bm, bd] = begin.split('-').map(Number);
  const [ey, em, ed] = end.split('-').map(Number);
  const cur = new Date(by, bm - 1, bd);
  const last = new Date(ey, em - 1, ed);
  if (Number.isNaN(cur.getTime()) || Number.isNaN(last.getTime()) || cur > last) return out;
  while (cur <= last) {
    const y = cur.getFullYear();
    const m = String(cur.getMonth() + 1).padStart(2, '0');
    const d = String(cur.getDate()).padStart(2, '0');
    out.push(`${y}-${m}-${d}`);
    cur.setDate(cur.getDate() + 1);
  }
  return out;
};

const VoiceNotifyDayQuery = () => {
  const [date, setDate] = useState(() => todayStr());
  const [withPreview, setWithPreview] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [rangeBegin, setRangeBegin] = useState(() => todayStr());
  const [rangeEnd, setRangeEnd] = useState(() => todayStr());
  const [rangeLoading, setRangeLoading] = useState(false);
  const [dailyRows, setDailyRows] = useState([]);

  const isToday = useMemo(() => String(date || '').trim() === todayStr(), [date]);

  const runQuery = async () => {
    const d = String(date || '').trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) {
      message.warning('日期写成 2026-07-14 这种');
      return;
    }
    setLoading(true);
    try {
      const res = await adminVoiceLowBalanceDayQuery({
        date: d,
        preview: withPreview && isToday ? 1 : 0,
      });
      if (res?.success) {
        setResult(res.data || null);
        message.success('查好了');
      } else {
        setResult(null);
        message.error(res?.msg || '查询失败');
      }
    } catch (e) {
      setResult(null);
      const isTimeout = e.code === 'ECONNABORTED' || String(e.message || '').includes('timeout');
      if (isTimeout) {
        message.error('超时了：可去掉「还要打谁」再查');
      } else if (e.response?.status === 404) {
        message.error('接口还没部署，请先更新后端');
      } else {
        message.error(e.response?.data?.msg || e.message || '查询失败');
      }
    } finally {
      setLoading(false);
    }
  };

  const history = Array.isArray(result?.history) ? result.history : [];
  const preview = result?.preview && Array.isArray(result.preview.list) ? result.preview.list : [];
  const dialStats = useMemo(() => calcDialStats(history), [history]);
  const doneCount = result?.history_total ?? history.length;
  const todoCount = result?.preview?.enabled ? (result.preview?.total ?? 0) : null;

  const runDailyRange = async () => {
    const begin = String(rangeBegin || '').trim();
    const end = String(rangeEnd || '').trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(begin) || !/^\d{4}-\d{2}-\d{2}$/.test(end)) {
      message.warning('日期写成 2026-07-14 这种');
      return;
    }
    const dates = listDatesInclusive(begin, end);
    if (!dates.length) {
      message.warning('开始日期不能晚于结束日期');
      return;
    }
    if (dates.length > 31) {
      message.warning('一次最多 31 天');
      return;
    }
    setRangeLoading(true);
    setDailyRows([]);
    try {
      const rows = [];
      for (const d of dates) {
        const res = await adminVoiceLowBalanceDayQuery({ date: d, preview: 0 });
        if (!res?.success) {
          rows.push({
            date: d,
            historyTotal: 0,
            publicOut: 0,
            publicAnswered: 0,
            publicRate: '—',
            exclusiveOut: 0,
            exclusiveAnswered: 0,
            exclusiveRate: '—',
            error: res?.msg || '失败',
          });
          continue;
        }
        rows.push({ date: d, ...calcDialStats(res.data?.history), error: '' });
      }
      setDailyRows(rows);
      message.success(`共 ${rows.length} 天`);
    } catch (e) {
      message.error(e.response?.data?.msg || e.message || '汇总失败');
    } finally {
      setRangeLoading(false);
    }
  };

  const dailyColumns = [
    { title: '日期', dataIndex: 'date', width: 120 },
    { title: '当天打电话人数', dataIndex: 'historyTotal', width: 120 },
    { title: '公共打出', dataIndex: 'publicOut', width: 90 },
    { title: '公共接听', dataIndex: 'publicAnswered', width: 90 },
    { title: '公共接听率', dataIndex: 'publicRate', width: 100 },
    { title: '专属打出', dataIndex: 'exclusiveOut', width: 90 },
    { title: '专属接听', dataIndex: 'exclusiveAnswered', width: 90 },
    { title: '专属接听率', dataIndex: 'exclusiveRate', width: 100 },
    {
      title: '备注',
      dataIndex: 'error',
      width: 120,
      ellipsis: true,
      render: (v) => (v ? <Text type="danger">{v}</Text> : '—'),
    },
  ];

  const historyColumns = [
    { title: '大概批次', dataIndex: 'batch_slot', width: 120 },
    { title: '入队时间', dataIndex: 'first_queued_at', width: 160 },
    { title: '手机号', dataIndex: 'phone', width: 120 },
    { title: '电表号', dataIndex: 'meter_no', width: 130, ellipsis: true },
    {
      title: '电量',
      dataIndex: 'remaining_kwh',
      width: 80,
      render: (v) => (v != null && v !== '' ? Number(v).toFixed(2) : '-'),
    },
    { title: '房间 fang', dataIndex: 'fang', width: 180, ellipsis: true },
    { title: '金额 qian', dataIndex: 'qian', width: 90 },
    { title: '表类型', dataIndex: 'meter_kind', width: 70 },
    {
      title: '公共电话',
      dataIndex: 'public_status',
      width: 90,
      render: (v) => <Tag color={statusColor(v)}>{v || '没打'}</Tag>,
    },
    {
      title: '专属电话',
      dataIndex: 'exclusive_status',
      width: 90,
      render: (v) => <Tag color={statusColor(v)}>{v || '没打'}</Tag>,
    },
    {
      title: '接了没',
      dataIndex: 'answer_label',
      width: 100,
      render: (v) => <Tag color={statusColor(v)}>{v || '—'}</Tag>,
    },
    { title: '设备ID', dataIndex: 'device_id', width: 80 },
  ];

  const previewColumns = [
    { title: '为什么会打', dataIndex: 'source_text', width: 130 },
    { title: '手机号', dataIndex: 'phone', width: 120 },
    { title: '电表号', dataIndex: 'meter_no', width: 130, ellipsis: true },
    {
      title: '电量',
      dataIndex: 'remaining_kwh',
      width: 80,
      render: (v) => (v != null && v !== '' ? Number(v).toFixed(2) : '-'),
    },
    { title: '房间 fang', dataIndex: 'fang', width: 180, ellipsis: true },
    { title: '金额 qian', dataIndex: 'qian', width: 90 },
    { title: '表类型', dataIndex: 'meter_kind', width: 70 },
    { title: '预计续打', dataIndex: 'next_call_at', width: 160, render: (v) => v || '-' },
  ];

  return (
    <div>
      <PageTitle title="电话通知查询" />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="查某一天：谁打了电话、接没接；今天还能看「接下来还要打谁」。只看数据，不会真打电话。"
      />

      <Card style={{ marginBottom: 16 }} title="① 查某一天">
        <Space wrap align="center">
          <Text>日期</Text>
          <Input
            value={date}
            onChange={(e) => setDate(e.target.value)}
            placeholder="2026-07-14"
            style={{ width: 140 }}
          />
          <Checkbox
            checked={withPreview}
            disabled={!isToday}
            onChange={(e) => setWithPreview(e.target.checked)}
          >
            同时看「还要打谁」（仅今天）
          </Checkbox>
          <Button type="primary" loading={loading} onClick={runQuery}>
            {loading ? '查询中…' : '查询这一天'}
          </Button>
          {result?.generated_at && (
            <Text type="secondary">{result.generated_at} 更新</Text>
          )}
        </Space>
      </Card>

      {result && (
        <>
          <Card
            style={{ marginBottom: 16 }}
            title={`② ${result.date} 这一天，先看总数`}
          >
            <Row gutter={[24, 16]}>
              <Col xs={24} sm={12} md={8}>
                <Statistic title="已经打过电话的人" value={doneCount} suffix="人" />
                <Text type="secondary">下面明细、接听率都算这些人</Text>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Statistic
                  title="接下来还要打的人"
                  value={todoCount == null ? '—' : todoCount}
                  suffix={todoCount == null ? '' : '人'}
                />
                <Text type="secondary">
                  {todoCount == null ? '未勾选或非今天，不查这个' : '待打/续打/下一巡检可能打'}
                </Text>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div style={{ lineHeight: 1.8 }}>
                  <div>系统打电话：{result.voice_enabled ? '开着' : '关着'}</div>
                  <div>公共没接再打专属：{result.exclusive_on ? '开着' : '关着'}</div>
                </div>
              </Col>
            </Row>

            <Divider style={{ margin: '16px 0' }} />

            <Text strong>从「已经打过电话的人」里统计</Text>
            <Row gutter={[24, 16]} style={{ marginTop: 12 }}>
              <Col xs={24} md={12}>
                <Card size="small" title="公共电话" type="inner">
                  <Space size="large" wrap>
                    <Statistic title="打出去" value={dialStats.publicOut} suffix="人" />
                    <Statistic title="接听了" value={dialStats.publicAnswered} suffix="人" />
                    <Statistic title="接听率" value={dialStats.publicRate} />
                  </Space>
                </Card>
              </Col>
              <Col xs={24} md={12}>
                <Card size="small" title="专属电话（公共没接才打）" type="inner">
                  <Space size="large" wrap>
                    <Statistic title="打出去" value={dialStats.exclusiveOut} suffix="人" />
                    <Statistic title="接听了" value={dialStats.exclusiveAnswered} suffix="人" />
                    <Statistic title="接听率" value={dialStats.exclusiveRate} />
                  </Space>
                </Card>
              </Col>
            </Row>
            <Text type="secondary" style={{ display: 'block', marginTop: 10 }}>
              「打出去」= 发出成功。专属接听率低很常见：很多人专属也没接，或结果还在同步。
            </Text>
          </Card>

          <Card
            title="③ 已经打过电话的人（明细）"
            style={{ marginBottom: 16 }}
            extra={<Text type="secondary">{doneCount} 人</Text>}
          >
            <Table
              rowKey={(r, i) => `${r.device_id}_${r.phone}_${i}`}
              columns={historyColumns}
              dataSource={history}
              size="small"
              scroll={{ x: 1400 }}
              pagination={{ pageSize: 50, showSizeChanger: true }}
            />
          </Card>

          {result.preview?.enabled ? (
            <Card
              title="④ 接下来还要打的人"
              style={{ marginBottom: 16 }}
              extra={<Text type="secondary">{todoCount} 人</Text>}
            >
              <Table
                rowKey={(r, i) => `p_${r.source}_${r.device_id}_${r.phone}_${i}`}
                columns={previewColumns}
                dataSource={preview}
                size="small"
                scroll={{ x: 1100 }}
                pagination={{ pageSize: 50, showSizeChanger: true }}
              />
            </Card>
          ) : null}
        </>
      )}

      <Card
        style={{ marginBottom: 16 }}
        title="⑤ 多天对比（每天一行）"
        extra={<Text type="secondary">最多 31 天，只统计「已经打过」</Text>}
      >
        <Space wrap align="center" style={{ marginBottom: dailyRows.length ? 12 : 0 }}>
          <Input
            value={rangeBegin}
            onChange={(e) => setRangeBegin(e.target.value)}
            placeholder="开始"
            style={{ width: 130 }}
          />
          <Text>到</Text>
          <Input
            value={rangeEnd}
            onChange={(e) => setRangeEnd(e.target.value)}
            placeholder="结束"
            style={{ width: 130 }}
          />
          <Button loading={rangeLoading} onClick={runDailyRange}>
            {rangeLoading ? '汇总中…' : '按天汇总'}
          </Button>
        </Space>
        {dailyRows.length > 0 && (
          <Table
            rowKey="date"
            columns={dailyColumns}
            dataSource={dailyRows}
            size="small"
            scroll={{ x: 1000 }}
            pagination={false}
          />
        )}
      </Card>
    </div>
  );
};

export default VoiceNotifyDayQuery;
